import React from 'react';
import { NavLink } from 'react-router-dom';
import { useAppState } from '../context/AppStateContext';
import { useHelpOptional } from '../help/HelpProvider';
import { ViewModeEnum, ViewMode } from '../types/viewMode';
import { viewToPath } from '../utils/viewRouting';
import { useSupportDiagnosticsAllowed } from '../services/supportDiagnosticsService';

/** Nav destinations that aren't one of Home's view modes. */
const ADMIN_PATH = '/admin';
const DIAGNOSTICS_PATH = '/support-diagnostics';

interface SidebarNavProps {
  /**
   * When supplied, link clicks are intercepted and routed through this
   * callback instead of navigating directly, so the host page can run its
   * unsaved-changes gate first. Receives the destination path plus the
   * ViewMode it maps to — `null` for Admin Dashboard, which is a page in its
   * own right rather than one of Home's view modes. Hosts that only need to
   * guard the navigation can ignore the second argument.
   */
  onNavigate?: (path: string, mode: ViewMode | null) => void;
  /** Extra classes for the <nav> — lets each host page own its bottom spacing. */
  className?: string;
}

/**
 * The left-hand navigation menu, shared by every page that renders a sidebar.
 * Lives on its own so the link list and its role gating exist in exactly one
 * place — Admin used to keep a hand-copied duplicate that silently drifted
 * (it never gained the Insights link, and gated nothing at all).
 */
const SidebarNav: React.FC<SidebarNavProps> = ({ onNavigate, className }) => {
  const { userRoleIds, roles, activeUser, selectedOrganisation, entitlements } = useAppState();
  // Optional: the nav is also rendered in tests and by hosts that sit outside
  // the help provider, and a missing help context must not break navigation.
  const help = useHelpOptional();

  // Source of truth for "is the current actor external" — same precedence as
  // Home/PostsGrid: trust the active user's flag, falling back to the org only
  // when an older token didn't carry isExternal. External users (parents etc.)
  // don't get the Weekly Schedule view.
  const isExternal =
    activeUser?.isExternal === true ||
    (activeUser?.isExternal === undefined && selectedOrganisation?.isExternal === true);

  // Rustle It support session: only the areas support may open (the API's
  // allow-list decides what actually works). Posts, Insights, Reports and
  // Documents are child content and are never shown, not shown-and-locked.
  const isSupport = activeUser?.isSupport === true;
  const diagnosticsAllowed = useSupportDiagnosticsAllowed();

  // We still render real anchors so right-click/open-in-new-tab work, and only
  // swallow the plain left-click when a host page asked to handle it.
  const handleNavClick = (e: React.MouseEvent, path: string, mode: ViewMode | null) => {
    if (!onNavigate) return;
    e.preventDefault();
    onNavigate(path, mode);
  };

  const viewClick = (mode: ViewMode) => (e: React.MouseEvent) =>
    handleNavClick(e, viewToPath(mode), mode);

  const itemClass = ({ isActive }: { isActive: boolean }) =>
    `sidebar-nav-item ${isActive ? 'active' : ''}`;

  return (
    <nav className={`sidebar-nav${className ? ` ${className}` : ''}`}>
      {!isSupport && (<NavLink
        to={viewToPath(ViewModeEnum.Dashboard)}
        data-help="nav.dashboard"
        className={itemClass}
        onClick={viewClick(ViewModeEnum.Dashboard)}
        end
      >
        <i className="fas fa-house" />
        <span>Dashboard</span>
      </NavLink>)}
      {!isSupport && (<NavLink
        to={viewToPath(ViewModeEnum.Grid)}
        className={itemClass}
        onClick={viewClick(ViewModeEnum.Grid)}
        data-help="nav.posts"
      >
        <i className="fas fa-pencil-alt" />
        <span>Add / Edit Posts</span>
      </NavLink>)}
      {!isExternal && (
        <NavLink
          to={viewToPath(ViewModeEnum.Schedule)}
          className={itemClass}
          onClick={viewClick(ViewModeEnum.Schedule)}
          data-help="nav.schedule"
        >
          <i className="fas fa-calendar-alt" />
          <span>Weekly Schedule</span>
        </NavLink>
      )}
      <NavLink
        to={viewToPath(ViewModeEnum.Attendance)}
        className={itemClass}
        onClick={viewClick(ViewModeEnum.Attendance)}
        data-help="nav.attendance"
      >
        <i className="fas fa-clipboard-check" />
        <span>Attendance</span>
      </NavLink>
      {/* Support fix attendance records; they have no need for the figures. */}
      {!isSupport && (<NavLink
        to={viewToPath(ViewModeEnum.AttendanceStats)}
        className={itemClass}
        onClick={viewClick(ViewModeEnum.AttendanceStats)}
      >
        <i className="fas fa-chart-pie" />
        <span>Attendance Stats</span>
      </NavLink>)}
      {/* Insights needs the Reporting role AND a plan that includes full AI.
          Deliberately absent rather than shown-and-locked: upsell-by-frustration
          is a poor fit for a budget-constrained SEND setting. If it isn't
          included, it isn't there. Home.tsx redirects /insights for the same
          reason — a nav gate alone leaves the page one address-bar edit away. */}
      {!isSupport && userRoleIds.includes(roles.Reporting) && entitlements.aiFullEnabled && (
        <NavLink
          to={viewToPath(ViewModeEnum.Insights)}
          className={itemClass}
          onClick={viewClick(ViewModeEnum.Insights)}
        >
          <i className="fas fa-chart-line" />
          <span>Insights</span>
        </NavLink>
      )}
      {!isSupport && userRoleIds.includes(roles.Reporting) && (
        <NavLink
          to={viewToPath(ViewModeEnum.Report)}
          className={itemClass}
          onClick={viewClick(ViewModeEnum.Report)}
          data-help="nav.reports"
        >
          <i className="fas fa-file-alt" />
          <span>Reports</span>
        </NavLink>
      )}
      {!isSupport && (<NavLink
        to={viewToPath(ViewModeEnum.Documents)}
        className={itemClass}
        onClick={viewClick(ViewModeEnum.Documents)}
        data-help="nav.documents"
      >
        <i className="fas fa-folder-open" />
        <span>Documents</span>
      </NavLink>)}
      {/* Post & report status: only when the Admin has switched it on for
          this grant (it follows a mid-session switch within a minute). */}
      {isSupport && diagnosticsAllowed === true && (
        <NavLink
          to={DIAGNOSTICS_PATH}
          className={itemClass}
          onClick={(e) => handleNavClick(e, DIAGNOSTICS_PATH, null)}
        >
          <i className="fas fa-stethoscope" />
          <span>Post &amp; Report Status</span>
        </NavLink>
      )}
      {userRoleIds.includes(roles.Admin) && (
        <NavLink
          to={ADMIN_PATH}
          className={itemClass}
          onClick={(e) => handleNavClick(e, ADMIN_PATH, null)}
          data-help="nav.admin"
        >
          <i className="fas fa-cog" />
          <span>Admin Dashboard</span>
        </NavLink>
      )}

      {/* Help is a utility, not a destination: a button (it opens the help
          window rather than navigating), pushed to the foot of the menu below
          a divider. It wears .sidebar-nav-item so the icon rail's rules apply
          to it unchanged — one tap on the rail icon opens help, with no
          dependence on the hover flyout that touchscreens don't have. */}
      {help && (
        <button
          type="button"
          className="sidebar-nav-item sidebar-nav-item--help"
          onClick={help.toggleHelp}
          title="Help & How Tos (F1)"
          aria-label="Help and How Tos"
          aria-expanded={help.open}
        >
          <i className="fas fa-circle-question" />
          <span>Help &amp; How Tos</span>
        </button>
      )}
    </nav>
  );
};

export default SidebarNav;
